import { IBIS, SPICE } from "../../../constants/libraryConstants";
import { END_TO_END_CHANNEL, PCB_CHANNEL } from "../../../constants/treeConstants";
import { getAMIComponents } from "../channel/AMIModelHelper";
import libraryConstructor from "../library/libraryConstructor";
import { controllerModelTypes, defaultPinModelInfo, deviceModelTypes, modelTypes } from "./constants";
import { splitTxModelType } from "./displayHelper";
import { getAMIComponents as getEndToEndAMIComponents } from "../endToEndChannel/AMIModelHelper";

class HspiceConfig {
  constructor({
    controller,
    device,
    signals,
    type = PCB_CHANNEL,
    controllerChannel = { channelId: "", designId: "", designName: "" },
    deviceChannel = { channelId: "", designId: "", designName: "" },
    symbolRate = "1 Gsps",
    initialDelay = "0 nsec",
    timeStep = "0.01 nsec",
    cycles = 200,
    hspiceCores = 2,
    eyeMask = { libraryId: "" },
    userDefinedNetlist = { libraryId: "" },
    testMode,
    enableSElement,
    eyePlotConfig = { waveform_start: "", waveform_end: "", ui_before_trigger: "1", ui_after_trigger: "0" },
    signalsList = []
  }) {
    this.symbolRate = symbolRate;
    this.initialDelay = initialDelay;
    this.timeStep = timeStep;
    this.cycles = cycles;
    this.hspiceCores = hspiceCores;
    this.eyeMask = eyeMask;
    this.userDefinedNetlist = userDefinedNetlist;
    this.testMode = typeof testMode === 'boolean' ? testMode : false;
    this.enableSElement = typeof enableSElement === 'boolean' ? enableSElement
      : type === END_TO_END_CHANNEL ? true : false;
    this.eyePlotConfig = eyePlotConfig;
    this.controller = controller;
    this.device = device;
    if (type === END_TO_END_CHANNEL) {
      this.controllerChannel = controllerChannel;
      this.deviceChannel = deviceChannel;
    }
    this.signals = (signals || []).map(signal => {
      const signalInfo = signalsList.find(item => item.name === signal.signalName);
      return new HspiceSignalConfig({ ...signal, signalInfo })
    })
  }

  setTerminationValue(signalName, pin, value, isCap) {
    const index = this.signals.findIndex(item => item.signalName === signalName);
    if (index < 0) return this;
    this.signals[index].setTerminationValue(pin, value, isCap);
    return this;
  }

  /**
   * The replacement target is another comp exchange of controller and post-processing of device, 
   * replacing the data inside txModel, rxModel, ctleModel
   * @param {*} controllerComp 
   * @param {*} deviceComp 
   * @param {*} channelType 
   * @param {*} controllerInfo 
   * @param {*} deviceInfo 
   * @param {*} signals 
   * @returns 
   */
  handleCompnentSwap({ controllerComp, deviceComp, channelType, controllerInfo, deviceInfo, signals }) {
    this.controller = controllerComp.name;
    this.device = deviceComp.name;

    if (channelType === END_TO_END_CHANNEL) {
      this.controllerChannel = { channelId: controllerInfo.id, designId: controllerInfo.designId, designName: controllerInfo.pcbName };
      this.deviceChannel = { channelId: deviceInfo.id, designId: deviceInfo.designId, designName: deviceInfo.pcbName };

      this.signals.forEach(signal => {
        let controllerPins = [], devicePins = [];
        for (const key of ['controller', 'device']) {
          const channelInfo = key === 'controller' ? controllerInfo : deviceInfo;
          if (channelInfo) {
            const { components } = channelInfo.content;
            const _signalInfo = getEndToEndSignalInfoBySignalName(channelInfo, signals, signal);
            const pinsInfo = handleComponents(
              components,
              _signalInfo,
              key === 'controller' ? this.controller : null,
              key === 'controller' ? null : this.device
            );
            if (key === 'controller') {
              controllerPins = pinsInfo.controllerPins;
            } else {
              devicePins = pinsInfo.devicePins;
            }
          }
        }

        signal.replacePinInModelsByDiffChannel('txModel', controllerPins, devicePins);
        for (const eleName of deviceModelTypes) {
          signal.replacePinInModelsByDiffChannel(eleName, devicePins, controllerPins);
        }
      })
      return;
    }

    this.signals.forEach(signal => {
      const signalInfo = signals.find(item => item.name === signal.signalName);
      if (controllerComp && controllerComp.pins) {
        signal.replacePinInModel('txModel', controllerComp, signalInfo);
      }
      if (deviceComp && deviceComp.pins) {
        for (const eleName of deviceModelTypes) {
          signal.replacePinInModel(eleName, deviceComp, signalInfo);
        }
      }
    });

    return this;
  }

  // The replacement object is not another comp
  handleComponentChange({ currType, resComp, channelType, compInfo, signals }) {
    this[currType] = resComp.name;
    if (channelType === END_TO_END_CHANNEL) {
      this[`${currType}Channel`] = { channelId: compInfo.id, designId: compInfo.designId, designName: compInfo.pcbName };
    }
    const eleNames = currType === 'controller' ? controllerModelTypes : deviceModelTypes;
    for (const eleName of eleNames) {
      this.signals.forEach(signal => {
        const signalInfo = channelType === END_TO_END_CHANNEL
          ? getEndToEndSignalInfoBySignalName(compInfo, signals, signal)
          : signals.find(item => item.name === signal.signalName);
        signal.replacePinInModel(eleName, resComp, signalInfo);
      });
    }

    return this;
  }

  setModelBySignalName(modelType, signalName, model) {
    const index = this.signals.findIndex(item => item.signalName === signalName);
    if (index < 0) return this;
    // Filter the files element as an empty object {}
    const files = [...model.files].filter(item => {
      return Object.keys(item).length > 0;
    });

    if (modelType === 'txModel') {
      // apply modelType to all
      this.signals.forEach((signal, i) => {
        if (i !== index) {
          signal = signal.setTxModelTypeToAll(model.type);
        }
      });
    }
    this.signals[index][modelType] = { ...model, files };
    return this;
  }

  setSignalModelNode(signalName, pin, node, eleName) {
    const index = this.signals.findIndex(item => item.signalName === signalName);
    if (index < 0) return this;
    const pairIndex = this.signals[index][eleName].pairs.findIndex(item => item.pin === pin);
    this.signals[index][eleName].pairs[pairIndex].node = node;
    return this;
  }

  applyToAllSignals({ modelType, model, signals, signalsList, signalInfo, channelType }) {
    // Filter the files element as an empty object {}
    const files = [...model.files].filter(item => {
      return Object.keys(item).length > 0;
    })
    const _model = { ...model, files };
    // Except that the pin and the net are all the same
    this.signals.forEach(signal => {
      signal = signal.setModel({ eleName: modelType, model: _model, signals, signalsList, _signalInfo: signalInfo, channelType })
    });
    return this;
  }

  // add or delete rxCpad or ctle
  setModelType(modelType, isShow) {
    this.signals.forEach(signal => {
      signal = signal.setModelType(modelType, isShow)
    });
    return this;
  }

  setStimulus(signalName, stimulus, applyAll) {
    if (applyAll) {
      for (let i = 0; i < this.signals.length; i++) {
        this.signals[i].stimulus = { ...stimulus };
      }
    } else {
      const index = this.signals.findIndex(item => item.signalName === signalName);
      if (index < 0) return this;
      this.signals[index].stimulus = { ...stimulus };
    }

    return this;
  }
}

class HspiceSignalConfig {
  constructor({
    type = PCB_CHANNEL,
    signalName = "",
    txModel,
    rxModel,
    rxCpadModel,
    ctleModel,
    termination,
    stimulus = { type: "PRBS", seed: 80, taps: [7, 6], length: 7, bitLength: 1000, delay: '1 nsec' },
    signalInfo = {}, /* Used for txModel to generate pinModels */
    controllerPins,
    devicePins,
    // controllerChannel = {}, // channelId, components
    // deviceChannel = {}
  }) {
    this.signalName = signalName;
    this.stimulus = stimulus;
    const modelList = { txModel, rxModel, rxCpadModel, ctleModel, termination };
    for (const eleName of modelTypes) {
      if (modelList[eleName]) {
        if (eleName === 'txModel' && !modelList[eleName].pinModels) {
          this[eleName] = {
            ...modelList[eleName],
            pinModels: initTxModelPinModels(modelList[eleName].pairs, signalInfo),
          }
          continue;
        }
        if (eleName === 'rxCpadModel') {
          modelList[eleName].pinModels = modelList[eleName].pinModels.map(item => ({ cpadValue: "1 pF", ...item }))
        }
        if (eleName === 'termination') {
          modelList[eleName].pinModels = modelList[eleName].pinModels.map(item => ({ impedance: "50 Ohm", ...item }))
          modelList[eleName].terminationCap = modelList[eleName].terminationCap || "22 pF";
        }
        this[eleName] = modelList[eleName];
      } else {
        const pins = eleName === 'txModel' ? controllerPins : devicePins;
        this.initModel({ type, eleName, pins, signalInfo });
      }
    }
  }

  initModel({ eleName, pins = [], signalInfo = {} }) {
    switch (eleName) {
      case 'txModel':
        this[eleName] = {
          // {fileName, libraryId, subckt}
          files: [],
          pairs: pins.map(pinInfo => ({ node: "", pin: pinInfo.pin, net: pinInfo.net })),
          pinModels: initTxModelPinModels(pins, signalInfo),
          type: SPICE // txModel: trio-ibis/duo-ibis/spice
        }
        break;
      case 'rxModel':
        this[eleName] = {
          // {fileName, libraryId, subckt, modelKey, path}
          files: [],
          // libraryId, fileName, subckt, Locate file from the files
          pairs: pins.map(pinInfo => ({ node: "", pin: pinInfo.pin, net: pinInfo.net })),
          // libraryId，fileName, Locate file from the files
          pinModels: pins.map(pinInfo => ({
            ...defaultPinModelInfo,
            pin: pinInfo.pin,
            net: pinInfo.net
          })),
          type: IBIS // spice / ibis
        }
        break;
      case 'rxCpadModel':
        this[eleName] = {
          // {fileName, libraryId, subckt, modelKey}
          files: [],
          pairs: pins.map(pinInfo => ({ node: "", pin: pinInfo.pin, net: pinInfo.net })),
          pinModels: pins.map(pinInfo => ({ cpadValue: "1 pF", pin: pinInfo.pin, net: pinInfo.net })),
          type: 'value' // txModel: spice / ibis, rxCapdModel: spice / value
        }
        break;
      case 'ctleModel':
        this[eleName] = {
          // {fileName, libraryId, subckt, modelKey}
          files: [],
          pairs: pins.map(pinInfo => {
            return [
              { node: "", pin: pinInfo.pin, net: pinInfo.net },
              { node: "", pin: `${pinInfo.pin || ""}_out`, net: pinInfo.net, originPin: pinInfo.pin || "" }
            ]
          }).flat(),
          type: SPICE// spice / ibis
        }
        break;
      case 'termination':
        this[eleName] = {
          // {fileName, libraryId, subckt}
          files: [],
          pinModels: pins.map(pinInfo => ({ impedance: "50 Ohm", pin: pinInfo.pin, net: pinInfo.net })),
          terminationCap: "22 pF",
          type: 'value' //  spice / value
        }
        break;
      default:
        break;
    }
    // if (type === END_TO_END_CHANNEL) {
    //   this[eleName].channelId = channelId || "";
    // }
  }

  setTerminationValue(pin, value, isCap) {
    if (isCap) {
      this.termination.terminationCap = value;
    } else {
      this.termination.pinModels = this.termination.pinModels.map(pinModel => {
        if (pinModel.pin === pin) {
          return {
            ...pinModel,
            impedance: value
          }
        }
        return pinModel
      })
    }
  }

  replacePinInModel(eleName, component, signalInfo) {
    if (this[eleName].pinModels && this[eleName].pinModels.length > 0) {
      this[eleName].pinModels = this[eleName].pinModels.map(pinModel => {
        const pin = component.pins.find(pinInfo => judgeSameNetType(pinModel.net, pinInfo.net, signalInfo)) || {};
        if (eleName === 'txModel') {
          const matchArr = pinModel.pin.match(/_[A-C][0-2]$/);
          const suffix = matchArr ? matchArr[0] : '';
          return { ...pinModel, pin: pin.pin + suffix, net: pin.net };
        } else {
          return { ...pinModel, pin: pin.pin, net: pin.net };
        }
      })
    }

    if (this[eleName].pairs && this[eleName].pairs.length > 0) {
      this[eleName].pairs = this[eleName].pairs.map(pair => {
        const pin = component.pins.find(pinInfo => judgeSameNetType(pinInfo.net, pair.net, signalInfo)) || {};
        if (pair.originPin) { // _out
          return { ...pair, pin: (pin.pin || "") + '_out', originPin: pin.pin, net: pin.net }
        } else {
          pair.pin = pin.pin;
          return { ...pair, originPin: '', net: pin.net };
        }
      })
    }
  }

  // apply to all set model
  setModel({ eleName, model, signals, signalsList, _signalInfo, channelType }) {
    const { files, pairs, type, pinModels } = model;

    let signalInfo = {};
    if (['txModel', 'ctleModel'].includes(eleName)) {
      let signalName = this.signalName;
      if (channelType === END_TO_END_CHANNEL) {
        signalName = (signals.find(signal => signal.name === signalName) || {})[`${eleName === 'txModel' ? 'channel1_signal' : 'channel2_signal'}`];
      }
      signalInfo = signalsList.find(item => item.name === signalName) || {};
    }
    switch (eleName) {
      case 'rxCpadModel':
      case 'rxModel':
        this[eleName].pinModels = this[eleName].pinModels.map((pinModel, index) => ({
          ...pinModels[index],
          pin: pinModel.pin,
          net: pinModel.net
        }))
        this[eleName].pairs = this[eleName].pairs.map((pair, index) => ({
          ...pairs[index],
          pin: pair.pin,
          net: pair.net,
        }))
        break;
      case 'txModel':
        if (/ibis/.test(type) && this[eleName].type !== type) {
          this[eleName].type = type;
          if (type === "duo-ibis") { // filter
            this[eleName].pinModels = this[eleName].pinModels.filter(item => {
              return !(/_[A-C]2$/.test(item.pin))
            })
          } else { // add
            this[eleName].pinModels = initTxModelPinModels(this[eleName].pairs, signalInfo, this[eleName].pinModels, true);
          }
        }
        this[eleName].pinModels = this[eleName].pinModels.map((pinModel) => {
          const suffix = pinModel.pin.match(/_[A-C][0-2]$/)[0];
          const netType = getNetType(pinModel, signalInfo);
          const pinModelInfo = pinModels.find(item => {
            const _suffix = item.pin.match(/_[A-C][0-2]$/)[0];
            const _netType = getNetType(item, _signalInfo);
            return _suffix === suffix && _netType === netType;
          });
          return {
            ...pinModelInfo,
            pin: pinModel.pin,
            net: pinModel.net
          }
        })
        this[eleName].pairs = this[eleName].pairs.map((pair, index) => ({
          ...pairs[index],
          pin: pair.pin,
          net: pair.net,
        }))
        break;
      case 'ctleModel':
        this[eleName].pairs = this[eleName].pairs.map((pair) => {
          const netType = getNetType(pair, signalInfo);
          const pairInfo = pairs.find(item => {
            const _netType = getNetType(item, _signalInfo);
            return _netType === netType && Boolean(item.originPin) === Boolean(pair.originPin);
          });
          if (pair.originPin) { // _out
            return { ...pairInfo, pin: pair.pin, net: pair.net, originPin: pair.originPin };
          }
          return { ...pairInfo, pin: pair.pin, net: pair.net };
        })
        break;
      default:
        break;
    }

    this[eleName].files = [...files];
    this[eleName].type = type;
    return this;
  }

  // rxCpad and ctle add or remove
  setModelType(eleName, value) {
    if (!value) {
      this[eleName].type = 'None';
    } else {
      switch (eleName) {
        case 'rxCpadModel':
          this[eleName].type = this[eleName].files.length > 0 ? SPICE : 'value';
          break;
        case 'rxModel':
          if (this[eleName].files.length > 0) {
            this[eleName].type = libraryConstructor.checkFile(IBIS, this[eleName].files[0].libraryId) ? IBIS : SPICE;
          } else {
            this[eleName].type = IBIS;
          }
          break;
        case 'ctleModel':
          this[eleName].type = SPICE;
          break;
        default:
          break;
      }
    }
    return this;
  }

  checkModel(eleName, pins, signalInfo) {
    const { pairs, pinModels, type } = this[eleName] || {};
    if (this[eleName]) {
      if (pairs && pairs.length > 0) {
        let _pairs = [];
        if (eleName === 'ctleModel') {
          _pairs = pins.map(pinInfo => {
            const pairInfo = pairs.find(pair => pair.pin === pinInfo.pin && judgeSameNetType(pair.net, pinInfo.net, signalInfo));
            const outPairInfo = pairs.find(pair => pair.originPin === pinInfo.pin && judgeSameNetType(pair.net, pinInfo.net, signalInfo));
            return [
              !pairInfo ? { node: "", pin: pinInfo.pin, net: pinInfo.net } : pairInfo,
              !outPairInfo ? { node: "", pin: `${pinInfo.pin || ""}_out`, net: pinInfo.net, originPin: pinInfo.pin } : outPairInfo
            ]
          }).flat();
        } else {
          _pairs = pins.map(pinInfo => {
            const pairInfo = pairs.find(pair => pair.pin === pinInfo.pin && judgeSameNetType(pair.net, pinInfo.net, signalInfo));
            return !pairInfo ? { node: "", pin: pinInfo.pin, net: pinInfo.net } : pairInfo;
          })
        }
        this[eleName].pairs = _pairs;
      } else if (['txModel', 'rxModel', 'rxCpadModel', 'ctleModel'].includes(eleName)) {
        this.initModel({ eleName, pins, signalInfo });
      }
      if (pinModels && pinModels.length > 0) {
        const _pinModels = pins.map(pinInfo => {
          if (eleName === 'txModel') {
            const pinModelInfo = pinModels.filter(item => {
              const matchArr = item.pin.match(/_[A-C][0-2]$/);
              const pinName = matchArr ? item.pin.slice(0, matchArr.index) : item.pin;
              return pinName === pinInfo.pin && judgeSameNetType(item.net, pinInfo.net, signalInfo);
            });

            if (pinModelInfo.length === 0) {
              const netType = getNetType(pinInfo, signalInfo);
              return Array.from({ length: type === 'duo-ibis' ? 2 : 3 }, (_, i) => ({
                ...defaultPinModelInfo,
                pin: `${pinInfo.pin}-${netType}${i}`,
                net: pinInfo.net
              }));
            }
            return pinModelInfo;
          }

          const pinModelInfo = pinModels.find(item => item.pin === pinInfo.pin && judgeSameNetType(item.net, pinInfo.net, signalInfo));
          if (!pinModelInfo) {
            switch (eleName) {
              case "rxModel":
                return { ...defaultPinModelInfo, pin: pinInfo.pin, net: pinInfo.net };
              case "termination":
                return { impedance: "50 Ohm", pin: pinInfo.pin, net: pinInfo.net };
              case "rxCpadModel":
                return { cpadValue: "1 pF", pin: pinInfo.pin, net: pinInfo.net };
              default:
                return {};
            }
          }
          return pinModelInfo;
        }).flat();
        this[eleName].pinModels = _pinModels;
      } else if (['txModel', 'rxModel', 'rxCpadModel', 'termination'].includes(eleName)) {
        this.initModel({ eleName, pins, signalInfo });
      }
    } else {
      this.initModel({ eleName, pins, signalInfo });
    }
    return this;
  }

  checkModels(controllerPins, devicePins, signalInfo) {
    for (const eleName of modelTypes) {
      this.checkModel(eleName, eleName === 'txModel' ? controllerPins : devicePins, signalInfo);
    }
    return this;
  }

  checkPin(newPin, prevPin) {
    for (const eleName of modelTypes) {
      if (this[eleName]) {
        const { pairs, pinModels } = this[eleName];
        if (pairs && pairs.length > 0) {
          const _pairs = pairs.map(pair => {
            if (pair.pin === prevPin.pin && pair.net === prevPin.net) {
              return { node: "", pin: newPin.pin, net: newPin.net };
            }

            if (eleName === 'ctleModel' && pair.originPin === prevPin.pin && pair.net === prevPin.net) { // _out
              return { node: "", pin: `${newPin.pin || ""}_out`, net: newPin.net, originPin: newPin.pin };
            }
            return pair;
          })
          this[eleName].pairs = _pairs;
        }
        if (pinModels && pinModels.length > 0) {
          const _pinModels = pinModels.map(pinModel => {
            let pinName = pinModel.pin, suffix = "";
            if (eleName === 'txModel') {
              const matchArr = pinModel.pin.match(/_[A-C][0-2]$/);
              suffix = matchArr ? matchArr[0] : '';
              pinName = matchArr ? pinModel.pin.slice(0, matchArr.index) : pinModel.pin;
            }
            if (pinName === prevPin.pin && pinModel.net === prevPin.net) {
              switch (eleName) {
                case 'txModel':
                  return { ...defaultPinModelInfo, pin: newPin.pin + suffix, net: newPin.net };
                case 'rxModel':
                  return { ...defaultPinModelInfo, pin: newPin.pin, net: newPin.net };
                case 'termination':
                  return { impedance: "50 Ohm", pin: newPin.pin, net: newPin.net };
                case 'rxCpadModel':
                  return { cpadValue: "1 pF", pin: newPin.pin, net: newPin.net };
                default:
                  return {};
              }
            }
            return pinModel;
          })
          this[eleName].pinModels = _pinModels;
        }
      }
    }
    return this;
  }

  checkComp(controllerPins, devicePins, replaceController, replaceDevice, signalInfo) {
    if (replaceController && controllerPins.length > 0) {
      for (const eleName of controllerModelTypes) {
        this.initModel({ eleName, pins: controllerPins, signalsList: [signalInfo] })
      }
    }

    if (replaceDevice && devicePins.length > 0) {
      for (const eleName of deviceModelTypes) {
        this.initModel({ eleName, pins: devicePins, signalsList: [signalInfo] })
      }
    }

    return this;
  }

  setTxModelTypeToAll(modelType) {
    const { modelType: paramsType } = splitTxModelType(modelType);
    const { modelType: type } = splitTxModelType(this.txModel.type);
    if (type !== paramsType) {
      const { pairs, pinModels, type } = this.txModel;
      if (/ibis/.test(type)) {
        this.txModel.pinModels = pinModels.map(item => {
          return { ...item, libraryId: "", fileName: "", modelName: "", modelType: "" }
        })
      } else {
        this.txModel.pairs = pairs.map(item => {
          return { node: "", pin: item.pin, net: item.net }
        })
      }
      this.txModel.files = [];
      this.txModel.type = modelType;
    }
    return this;
  }

  clearModels(types) {
    for (const eleName of (types || modelTypes)) {
      if (this[eleName].pairs && this[eleName].pairs.length > 0) {
        this[eleName].pairs = [];
      }

      if (this[eleName].pinModels && this[eleName].pinModels.length > 0) {
        this[eleName].pinModels = [];
      }
    }
  }

  // replace between two channels
  replacePinInModelsByDiffChannel(eleName, pins = [], oldPins = []) {
    // pairs
    if (eleName === 'ctleModel') {
      const pairs = pins.map((pinInfo, index) => {
        const oldPair = this[eleName].pairs.find(item => item.pin === oldPins[index].pin && item.net === oldPins[index].net) || {};
        const oldOutPair = this[eleName].pairs.find(item => item.pin === `${oldPins[index].pin || ""}_out` && item.net === oldPins[index].net) || {};

        return [
          { ...oldPair, pin: pinInfo.pin, net: pinInfo.net },
          { ...oldOutPair, pin: `${pinInfo.pin || ""}_out`, net: pinInfo.net, originPin: pinInfo.pin },
        ]
      }).flat();

      this[eleName].pairs = pairs;
    } else if (eleName !== 'termination') {
      const pairs = pins.map((pinInfo, index) => {
        const oldPair = this[eleName].pairs.find(item => item.pin === oldPins[index].pin && item.net === oldPins[index].net) || {};
        return { ...oldPair, pin: pinInfo.pin, net: pinInfo.net };
      })

      this[eleName].pairs = pairs;
    }

    // pinModels
    if (eleName === 'txModel') {
      const pinModels = pins.map((pinInfo, index) => {
        const netType = String.fromCharCode(65 + index);
        const oldPinModel0 = this[eleName].pinModels.find(item => item.pin === `${oldPins[index].pin}_${netType}0` && item.net === oldPins[index].net) || {};
        const oldPinModel1 = this[eleName].pinModels.find(item => item.pin === `${oldPins[index].pin}_${netType}1` && item.net === oldPins[index].net) || {};

        if (this[eleName].type !== 'trio-ibis') {
          return [
            { ...oldPinModel0, pin: `${pinInfo.pin}_${netType}0`, net: pinInfo.net },
            { ...oldPinModel1, pin: `${pinInfo.pin}_${netType}1`, net: pinInfo.net }
          ]
        } else {
          const oldPinModel2 = this[eleName].pinModels.find(item => item.pin === `${oldPins[index].pin}_${netType}2` && item.net === oldPins[index].net) || {};
          return [
            { ...oldPinModel0, pin: `${pinInfo.pin}_${netType}0`, net: pinInfo.net },
            { ...oldPinModel1, pin: `${pinInfo.pin}_${netType}1`, net: pinInfo.net },
            { ...oldPinModel2, pin: `${pinInfo.pin}_${netType}2`, net: pinInfo.net },
          ]
        }
      }).flat();

      this[eleName].pinModels = pinModels;
    } else if (eleName !== 'ctleModel') {
      const pinModels = pins.map((pinInfo, index) => {
        const oldPinModel = this[eleName].pinModels.find(item => item.pin === oldPins[index].pin && item.net === oldPins[index].net) || {};
        return { ...oldPinModel, pin: pinInfo.pin, net: pinInfo.net };
      })

      this[eleName].pinModels = pinModels;
    }
  }
}

function initChannelHspiceConfig({ components, signals }) {
  const { controller, device } = getAMIComponents(components);

  const hspiceSignals = initDefaultHspiceSignals({
    signals: signals,
    controller,
    device,
    components
  });
  const hspiceConfig = new HspiceConfig({
    signals: hspiceSignals,
    controller,
    device
  });
  return hspiceConfig;
}

function initDefaultHspiceSignals({
  signals,
  controller,
  device,
  components
}) {
  let hspiceSignals = [];
  // const _signals = signals.filter(item => selectedSignals.includes(item.name));
  for (let signal of signals) {
    const { controllerPins, devicePins } = handleComponents(components, signal, controller, device);
    hspiceSignals.push(
      new HspiceSignalConfig({
        signalName: signal.name,
        signalInfo: signal,
        controllerPins,
        devicePins
      })
    )
  }
  return hspiceSignals;
}

function initEndToEndChannelHspiceConfig(setupInfo, { channelSetupList = [] }) {
  if (!setupInfo || !setupInfo.content || !setupInfo.content.pcbConnections) {
    return setupInfo;
  }

  const { firstComp, endComp } = getEndToEndAMIComponents({
    pcbConnections: setupInfo.content.pcbConnections,
    connections: setupInfo.content.connections,
    channelSetupList
  });

  const hspiceSignals = initEndToEndDefaultHspiceSignals({ firstComp, endComp });

  const hspiceConfig = new HspiceConfig({
    signals: hspiceSignals,
    controller: firstComp.component,
    controllerChannel: firstComp.channel,
    deviceChannel: endComp.channel,
    device: endComp.component,
    type: END_TO_END_CHANNEL
  });

  return hspiceConfig;
}

function initEndToEndDefaultHspiceSignals({ firstComp, endComp }) {

  let signals = [], controllerComp = {}, deviceComp = {};
  if (firstComp.channel && firstComp.channel.channelId) {
    signals = firstComp.signal_connections_map || [];
    controllerComp = firstComp;
  }

  if (endComp.channel && endComp.channel.channelId) {
    signals = endComp.signal_connections_map || [];
    deviceComp = endComp;
  }
  let hspiceSignals = [];
  for (let signal of signals) {

    let controllerPins = [], devicePins = [], signalInfo = {};

    for (const key of ['controller', 'device']) {
      const compInfo = key === 'controller' ? controllerComp : deviceComp;
      if (compInfo.channelInfo) {
        const signalMap = (key === 'controller' ? (firstComp.signal_connections_map || []) : (endComp.signal_connections_map || []))
          .find(item => item.name === signal.name);
        const { components, signals: _signals } = compInfo.channelInfo.content;
        const signalName = signalMap[compInfo.signalKey];
        const _signalInfo = _signals.find(item => item.name === signalName);
        const pinsInfo = handleComponents(
          components,
          _signalInfo,
          key === 'controller' ? compInfo.component : null,
          key === 'controller' ? null : compInfo.component
        );
        if (key === 'controller') {
          controllerPins = pinsInfo.controllerPins;
          signalInfo = _signalInfo;
        } else {
          devicePins = pinsInfo.devicePins;
        }
      }
    }

    hspiceSignals.push(new HspiceSignalConfig({
      type: END_TO_END_CHANNEL,
      signalName: signal.name,
      controllerPins,
      devicePins,
      signalInfo
    }))
  }

  return hspiceSignals;
}

function packageHspiceConfig(hspiceConfig, type = PCB_CHANNEL, signalsList = []) {
  return new HspiceConfig({ ...hspiceConfig, type, signalsList });
}

function handleComponents(components = [], signal = {}, controller, device) {
  const { nets_A = [], nets_B = [], nets_C = [] } = signal;
  function getPins(comp = {}) {
    if (!comp.pins || !comp.pins.length) {
      return [];
    }
    let a_pin, b_pin, c_pin;
    for (let item of comp.pins) {
      if (nets_A.includes(item.net) && !a_pin) {
        a_pin = { ...item, component: comp.name }
      }
      if (nets_B.includes(item.net) && !b_pin) {
        b_pin = { ...item, component: comp.name };
      }
      if (nets_C.includes(item.net) && !c_pin) {
        c_pin = { ...item, component: comp.name };
      }

      if (a_pin && b_pin && c_pin) {
        break;
      }
    }
    return [a_pin || {}, b_pin || {}, c_pin || {}];
  }
  const controllerComp = controller ? components.find(item => item.name === controller) : {};
  const deviceComp = device ? components.find(item => item.name === device) : {};

  return { controllerPins: getPins(controllerComp), devicePins: getPins(deviceComp) };
}

// signalInfo: [nets_A, nets_B, nets_C]
function initTxModelPinModels(controllerPins, signalInfo, _pinModels, isOnlyGenerate2) {
  return controllerPins.map(pinInfo => {
    const defaultInfo = { ...defaultPinModelInfo, net: pinInfo.net };
    const netType = getNetType(pinInfo, signalInfo)

    if (isOnlyGenerate2) {
      const pinModel0 = _pinModels.find(item => item.pin === pinInfo.pin + `_${netType}0`);
      const pinModel1 = _pinModels.find(item => item.pin === pinInfo.pin + `-${netType}1`);
      return [
        pinModel0 || { ...defaultInfo, pin: pinInfo.pin + `_${netType}0` },
        pinModel1 || { ...defaultInfo, pin: pinInfo.pin + `_${netType}1` },
        { ...defaultInfo, pin: pinInfo.pin + `_${netType}2` },
      ]
    }

    return [
      { ...defaultInfo, pin: pinInfo.pin + `_${netType}0` },
      { ...defaultInfo, pin: pinInfo.pin + `_${netType}1` },
      // { ...defaultInfo, pin: pinInfo.pin + `-${netType}2` }
    ]
  }).flat()
}

function getNetType(pinModel, signalInfo) {
  let netType = 'A';
  for (const ele of ['A', 'B', 'C']) {
    if ((signalInfo[`nets_${ele}`] || []).includes(pinModel.net)) {
      netType = ele;
      break;
    }
  }
  return netType;
}

function judgeSameNetType(net1, net2, signalInfo) {
  let isSame = false;
  for (const ele of ['A', 'B', 'C']) {
    if ((signalInfo[`nets_${ele}`] || []).includes(net1) && (signalInfo[`nets_${ele}`] || []).includes(net2)) {
      isSame = true;
      break;
    }
  }
  return isSame;
}

function getEndToEndSignalInfoBySignalName(channelInfo, signals, signal) {

  function getSignalName(channelId, signalName) {
    const signal = signals.find(item => item.name === signalName)
    if (channelId === signal.channel1_id) {
      return signal.channel1_signal;
    } else if (channelId === signal.channel2_id) {
      return signal.channel2_signal;
    }

    return "";
  }

  const { signals: _signals } = channelInfo.content;
  const signalName = getSignalName(channelInfo.id, signal.signalName);
  const _signalInfo = _signals.find(item => item.name === signalName);

  return _signalInfo;
}

export {
  HspiceConfig,
  HspiceSignalConfig,
  initChannelHspiceConfig,
  initDefaultHspiceSignals,
  packageHspiceConfig,
  handleComponents,
  initTxModelPinModels,
  initEndToEndChannelHspiceConfig
}